import _ from 'underscore';
import { Component, HostBinding, Input, OnInit } from '@angular/core';
import { LoadingProgressor } from '@ark7/loading';

@Component({
  selector: 'a7-button',
  templateUrl: './a7-button.component.html',
  styleUrls: ['./a7-button.component.scss'],
  standalone: false,
})
export class A7ButtonComponent implements OnInit {
  @Input()
  @HostBinding('class')
  widthClass = 'v2-min-w-9';

  _size: A7ButtonSize = 'medium';
  @Input() set size(v: A7ButtonSize) {
    if (this._size === v) {
      return;
    }
    this._size = v;
    this.refreshClasses();
  }
  get size() {
    return this._size;
  }
  _theme: A7ButtonStyle = 'filled-medium';
  @Input() set theme(v: A7ButtonStyle) {
    if (this._theme === v) {
      return;
    }
    this._theme = v;
    this.refreshClasses();
  }

  @Input() progressor: LoadingProgressor = new LoadingProgressor();

  get theme() {
    return this._theme;
  }

  @Input()
  icon?: string;

  @Input()
  inverse?: boolean = false;

  // disabled state is stored in separated boolean.
  // you can hover/click on a disabled button, etc, without any effects.
  _interactiveState: Omit<A7ButtonState, 'disabled'> = 'standard';
  set state(v: A7ButtonState) {
    if (this._interactiveState === v) {
      return;
    }
    this._interactiveState = v;
    this.refreshClasses();
  }

  get state(): A7ButtonState {
    return this.disabled
      ? 'disabled'
      : (this._interactiveState as A7ButtonState);
  }

  _disabled = false;
  @Input()
  set disabled(v: boolean) {
    if (v === this._disabled) {
      return;
    }
    this._disabled = v;
    this.refreshClasses();
  }
  get disabled() {
    return this._disabled;
  }

  classes = [];

  RippleColor = RippleColor;
  ngOnInit(): void {
    this.refreshClasses();
  }

  refreshClasses() {
    const ret = ['inline-block', 'flex-row-center-center'].concat(
      !_.isEmpty(this.widthClass)
        ? [!this.widthClass.includes('min') ? 'full-width' : this.widthClass]
        : [],
    );

    [
      Radius[this._size],
      Padding[this._size],
      this._theme == 'text-medium' ? 'text-link-md' : FontSize[this._size],
      RowGap[this._size],
      Cursor[this.state],
      fontColor(this._theme, this.state),
      bgColor(this._theme, this.state, this.inverse),
      borderStyle(this._theme, this.state),
      opacity(this._theme, this.state),
    ].forEach((t) => {
      ret.push(...(t?.split(' ') ?? []));
    });

    this.classes = ret;
  }
  /**
   * stop event propagation if disabled
   * @param e
   * @returns
   */
  onClick(e) {
    if (this.disabled || this.progressor.isLoading) {
      e.stopPropagation();
      return;
    }
  }
}
export type A7ButtonSize = 'mini' | 'small' | 'medium' | 'large' | 'xl';
export type A7ButtonStyle =
  | 'filled-heavy'
  | 'filled-medium'
  | 'filled-medium-danger'
  | 'filled-xlight'
  | 'text-medium-danger'
  | 'outlined-light'
  | 'elevated-light'
  | 'text-medium';

export type A7ButtonState = 'standard' | 'hovered' | 'pressed' | 'disabled';

const Radius = {
  mini: 'border-radius-full',
  small: 'border-radius-full',
  medium: 'border-radius-full',
  large: 'border-radius-full',
  xl: 'border-radius-full',
};

const RowGap = {
  mini: 'v2-row-gap-4px',
  small: 'v2-row-gap-4px',
  medium: 'v2-row-gap-8px',
  large: 'v2-row-gap-8px',
  xl: 'v2-row-gap-8px',
};

const Padding = {
  mini: 'v2-padding-1-h v2-padding-6px-v',
  small: 'v2-padding-16px-h v2-padding-10px-v',
  medium: 'v2-padding-20px-h v2-padding-10px-v',
  large: 'v2-padding-2-h v2-padding-16px-v',
  xl: 'v2-padding-32px-h v2-padding-20px-v',
};

const FontSize = {
  mini: 'label-s',
  small: 'label-s',
  medium: 'button-md',
  large: 'label-l',
  xl: 'h3',
};

const Cursor = {
  standard: 'pointer',
  hovered: 'pointer',
  pressed: 'wait',
  disabled: 'disabled',
};

const RippleColor = {
  'filled-heavy': '#FFFFFF80',
  'outlined-light': '#FFFFFF80',
  'elevated-light': '#FFFFFF80',
  'filled-xlight': '#FFFFFF80',
  'filled-medium': '#FFFFFF80',
  'filled-medium-danger': '#FFFFFF80',
  'text-medium-danger': '#FFFFFF80',
  'text-medium': '#FFFFFF80',
};

export const LogoSize = {
  mini: 'v2-h-16px',
  small: 'v2-h-20px',
  medium: 'v2-h-2',
  large: 'v2-h-28px',
  xl: 'v2-h-32px',
};

function fontColor(style: A7ButtonStyle, state: A7ButtonState): string {
  if (state === 'disabled') {
    return 'color-on-surface-variant-subtle';
  }

  if (['filled-heavy'].includes(style)) {
    return 'color-on-primary';
  }

  if (['filled-medium'].includes(style)) {
    return 'color-inverse-on-surface';
  }

  if (['filled-medium-danger'].includes(style)) {
    return state === 'standard' ? 'color-error-on-container' : 'color-on-error';
  }

  if (['text-medium-danger'].includes(style)) {
    return 'color-error-on-container';
  }

  if (style === 'elevated-light') {
    return 'color-inverse-on-surface';
  }

  if (['outlined-light'].includes(style)) {
    return 'color-on-surface';
  }

  if (['filled-xlight'].includes(style)) {
    return 'color-on-surface';
  }

  if (['text-medium'].includes(style)) {
    return state === 'standard'
      ? 'color-on-surface-variant'
      : 'color-on-surface';
  }
}

function opacity(style: A7ButtonStyle, state: A7ButtonState) {
  if (state == 'disabled') {
    return null;
  } else if (style === 'elevated-light') {
    return ['hovered', 'pressed'].includes(state) ? '' : 'opacity-90';
  } else {
    return null;
  }
}

function bgColor(
  style: A7ButtonStyle,
  state: A7ButtonState,
  inverse: boolean,
): string {
  if (state == 'disabled') {
    if (style === 'text-medium-danger' || style === 'text-medium') {
      return 'bg-transparent';
    }
    return inverse ? 'bg-inverse-on-surface-12' : 'bg-surface-variant';
  }

  switch (style) {
    case 'filled-heavy': {
      switch (state) {
        case 'standard':
          return 'bg-primary';
        case 'hovered':
        case 'pressed':
          return 'bg-600';
      }
      break;
    }
    case 'filled-medium-danger': {
      switch (state) {
        case 'standard':
          return 'bg-error-container';
        case 'hovered':
        case 'pressed':
          return 'bg-error';
      }
      break;
    }
    case 'outlined-light': {
      switch (state) {
        case 'standard':
          return 'bg-white';
        case 'hovered':
        case 'pressed':
          return 'bg-blue-grey-1';
      }
      break;
    }
    case 'elevated-light': {
      switch (state) {
        case 'standard':
          return 'bg-inverse-surface';
        case 'hovered':
        case 'pressed':
          return 'bg-inverse-surface';
      }
      break;
    }

    case 'filled-xlight': {
      switch (state) {
        case 'standard':
          return 'bg-surface';
        case 'hovered':
        case 'pressed':
          return 'bg-blue-grey-1';
      }
      break;
    }

    case 'filled-medium': {
      switch (state) {
        case 'standard':
        case 'hovered':
        case 'pressed':
          return 'bg-inverse-surface';
      }
      break;
    }
    case 'text-medium-danger': {
      switch (state) {
        case 'standard':
          return 'bg-transparent';
        case 'hovered':
        case 'pressed':
          return 'bg-error-container';
      }
    }
    case 'text-medium': {
      return 'bg-transparent';
    }
  }
}

function borderStyle(style: A7ButtonStyle, state: A7ButtonState): string {
  switch (style) {
    case 'filled-heavy': {
      switch (state) {
        case 'standard':
        case 'hovered':
        case 'pressed':
        case 'disabled':
          return 'v2-border-0px';
      }
      break;
    }
    case 'filled-medium-danger': {
      switch (state) {
        case 'standard':
        case 'hovered':
        case 'pressed':
        case 'disabled':
          return 'v2-border-0px';
      }
      break;
    }
    case 'outlined-light': {
      switch (state) {
        case 'standard':
        case 'hovered':
        case 'pressed':
          return 'border-06px border-color-outline-variant';
        case 'disabled':
          return 'border-06px border-on-surface-opacity-12';
      }
      break;
    }
    case 'elevated-light': {
      switch (state) {
        case 'standard':
        case 'hovered':
        case 'pressed':
          return 'no-border elevation-1dp';
        case 'disabled':
          return 'v2-border-0px';
      }
    }
    case 'filled-medium': {
      switch (state) {
        case 'standard':
        case 'hovered':
        case 'pressed':
        case 'disabled':
          return 'v2-border-0px';
      }
      break;
    }
    case 'text-medium-danger': {
      switch (state) {
        case 'standard':
        case 'hovered':
        case 'pressed':
        case 'disabled':
          return 'v2-border-0px';
      }
      break;
    }
    case 'filled-xlight': {
      switch (state) {
        case 'standard':
        case 'hovered':
        case 'pressed':
        case 'disabled':
          return 'v2-border-0px';
      }
      break;
    }
    case 'text-medium': {
      switch (state) {
        case 'standard':
        case 'hovered':
        case 'pressed':
        case 'disabled':
          return 'v2-border-0px';
      }
      break;
    }
  }
}
